import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowBendDownRight,
  ArrowsDownUp,
  CaretDown,
  CaretRight,
  CaretUp,
  Check,
  DeviceMobile,
  Envelope,
  FileXls,
  Funnel,
  Minus,
  Plus,
  Trash,
  User,
  UsersThree,
  Warning,
  X,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';

import {
  listDependents,
  listGuardianIDsForFilter,
  listGuardians,
  listMembers,
  type Guardian,
  type GuardianRelationship,
  type Member,
  type MemberMembership,
} from '@/api/members';
import { Badge } from '@/components/ui/badge';
import { MemberAvatar } from '@/components/MemberAvatar';
import { LoadError } from '@/components/LoadError';
import { QuickEmailDialog } from '@/components/QuickEmailDialog';
import { approveCommand, createCommand } from '@/api/commands';
import { listMyOrganizations } from '@/api/orgs';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { SearchInput } from '@/components/ui/search-input';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import {
  TablePagination,
  useTablePaginationState,
} from '@/components/ui/table-pagination';
import {
  SortableHeaderButton,
  useTableSortState,
  type TableSortState,
} from '@/components/ui/table-sort';
import {
  RowActions,
  StickyEndCell,
  useHorizontalScrollEdge,
  type RowAction,
} from '@/components/ui/row-actions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { EditColumnsDropdown } from '@/components/EditColumnsDropdown';
import { AddMemberModal } from '@/components/AddMemberModal';
import { MemberCard } from '@/components/MemberCard';
import { MemberFilterPanel, countActiveFilters, EMPTY_FILTERS, warningsToApiParams, type MemberFilters } from '@/components/MemberFilterPanel';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  COLUMN_LABEL_KEY,
  DEFAULT_COLUMN_PREFS,
  loadColumnPrefs,
  resetColumnPrefs,
  saveColumnPrefs,
  visibleColumnsInOrder,
  type ColumnId,
  type ColumnPref,
} from '@/utils/memberColumns';

// Members lists every member of the org as a row in a tabular layout. Each
// row carries:
//   - a left chevron column that expands inline to show guardians, but only
//     when the member actually has any (driven by member.has_guardians)
//   - a sticky-right ActionsCell with a 3-dot menu of row actions; on hover
//     (md+) any "fast actions" surface as inline icon buttons
//
// The chevron column reserves 32px on every row so columns line up regardless
// of which rows are expandable.

export function Members() {
  const { slug = '' } = useParams();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [columnPrefs, setColumnPrefs] = useState<ColumnPref[]>(() => [
    ...DEFAULT_COLUMN_PREFS,
  ]);
  const [addOpen, setAddOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<MemberFilters>(EMPTY_FILTERS);
  const activeFilterCount = countActiveFilters(filters);

  // MemberCard side-panel state is URL-driven (?member=<id>) so the AI
  // assistant's navigate tool can deep-link "open Anna Karlsson's profile"
  // directly to the panel. Non-list callers (AddMemberModal's "Open
  // existing person" affordance on a 409 personal_id_exists hit) drive it
  // via the same openCardFor helper, which now updates the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const cardMemberID = searchParams.get('member');
  const openCardFor = useCallback(
    (memberID: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('member', memberID);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const closeCard = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('member');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Selection state lifted here so the header Actions button can access it.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedGuardians, setSelectedGuardians] = useState<Set<string>>(new Set());
  const [selectedAllMatching, setSelectedAllMatching] = useState(false);

  function clearSelection() {
    setSelected(new Set());
    setSelectedGuardians(new Set());
    setSelectedAllMatching(false);
  }

  useEffect(() => {
    if (!slug) return;
    setColumnPrefs(loadColumnPrefs(slug));
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    saveColumnPrefs(slug, columnPrefs);
  }, [slug, columnPrefs]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);

  const { page, size, setPage, setSize, offset, limit } = useTablePaginationState({
    defaultSize: 25,
  });

  // Sortable column allowlist mirrors the backend's memberSortColumns map
  // in internal/services/members/service.go. Pinned columns (first/last
  // name) are sortable too — they're the most-used sort keys.
  const sortState = useTableSortState<ColumnId>({
    columns: [
      'first_name',
      'last_name',
      'personal_id',
      'email',
      'phone',
      'street_address',
      'postal_code',
      'city',
    ] as const,
  });

  // Reset to page 1 whenever the (debounced) search or filters change.
  useEffect(() => {
    setPage(1);
    // setPage is stable; deps intentionally exclude it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters]);

  const membersQuery = useQuery({
    queryKey: [
      'members',
      slug,
      debouncedSearch,
      limit,
      offset,
      sortState.sort,
      sortState.dir,
      filters,
    ],
    queryFn: () =>
      listMembers(token, slug, {
        search: debouncedSearch,
        limit,
        offset,
        ...(sortState.sort
          ? { sort: sortState.sort, order: sortState.dir }
          : {}),
        ...(filters.teamIds.length > 0 ? { teamIds: filters.teamIds } : {}),
        ...(filters.personalIdStatuses.length > 0 ? { personalIdStatuses: filters.personalIdStatuses } : {}),
        ...warningsToApiParams(filters.warnings),
        ...(filters.isLeader ? { isLeader: true } : {}),
        ...(filters.includeGuardians ? { includeGuardians: true } : {}),
        ...(filters.createdAfter ? { createdAfter: filters.createdAfter } : {}),
        ...(filters.createdBefore ? { createdBefore: filters.createdBefore } : {}),
        ...(filters.paymentStatus ? { paymentStatus: filters.paymentStatus } : {}),
        ...(filters.paymentStatus && filters.costTypeId ? { costTypeId: filters.costTypeId } : {}),
      }),
    enabled: !!token && !!slug,
    placeholderData: (prev) => prev,
  });

  // Prefetch guardians for every member on this page that has them, so
  // expanding a row is instant rather than showing a loading state.
  useEffect(() => {
    if (!token || !slug || !membersQuery.data) return;
    for (const member of membersQuery.data.members) {
      if (!member.has_guardians) continue;
      void queryClient.prefetchQuery({
        queryKey: ['guardians', slug, member.id],
        queryFn: () => listGuardians(token, slug, member.id),
      });
    }
  }, [membersQuery.data, token, slug, queryClient]);

  // Add / import are owner+administrator. Leaders manage existing roster
  // entries on their teams but can't seed brand-new people into the org.
  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const callerRole = orgsQuery.data?.find((o) => o.slug === slug)?.role ?? '';
  const canAddMembers = callerRole === 'owner' || callerRole === 'administrator';

  const visibleColumns = useMemo(() => visibleColumnsInOrder(columnPrefs), [columnPrefs]);

  const isUnauthorized =
    membersQuery.isError &&
    membersQuery.error instanceof ApiError &&
    membersQuery.error.status === 404;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 p-4 md:p-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('nav.members')}
          </p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
            {t('members.title')}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Column-editing is a power-user setting that has no real
              utility on a narrow viewport (only a couple of columns
              fit anyway), so we hide it below sm: to keep the action
              row from wrapping. */}
          <div className="hidden sm:block">
            <EditColumnsDropdown
              prefs={columnPrefs}
              onChange={setColumnPrefs}
              onReset={() => setColumnPrefs(resetColumnPrefs(slug))}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setFilterOpen(true)}
            className="relative"
            aria-label={t('members.filter.button')}
          >
            <Funnel className="h-4 w-4" />
            <span className="hidden sm:inline">{t('members.filter.button')}</span>
            {activeFilterCount > 0 && (
              <span className="flex h-4 min-w-4 items-center justify-center rounded-pill bg-primary px-1 font-heading text-[0.6rem] font-bold leading-none text-primary-foreground">
                {activeFilterCount}
              </span>
            )}
          </Button>
          {canAddMembers && (
            <Button type="button" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('members.addCta')}
            </Button>
          )}
        </div>
      </header>

      <SearchInput
        placeholder={t('members.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label={t('members.searchPlaceholder')}
        clearLabel={t('common.clearSearch')}
      />

      {membersQuery.isLoading && (
        <TableSkeleton columns={visibleColumns.length + 2} />
      )}

      {isUnauthorized && (
        <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {t('members.notFound')}
        </p>
      )}

      {membersQuery.isError && !isUnauthorized && (
        <LoadError onRetry={() => void membersQuery.refetch()} />
      )}

      {membersQuery.data && membersQuery.data.members.length === 0 && (
        <MembersEmpty {...(canAddMembers ? { onAdd: () => setAddOpen(true) } : {})} />
      )}

      {membersQuery.data && membersQuery.data.members.length > 0 && (
        <>
          <MemberList
            members={membersQuery.data.members}
            total={membersQuery.data.total}
            search={debouncedSearch}
            columns={visibleColumns}
            slug={slug}
            sortState={sortState}
            selected={selected}
            selectedGuardians={selectedGuardians}
            selectedAllMatching={selectedAllMatching}
            setSelected={setSelected}
            setSelectedGuardians={setSelectedGuardians}
            setSelectedAllMatching={setSelectedAllMatching}
            onClearSelection={clearSelection}
            openCardFor={openCardFor}
          />
          <TablePagination
            total={membersQuery.data.total}
            page={page}
            size={size}
            onPageChange={setPage}
            onSizeChange={setSize}
          />
        </>
      )}

      <AddMemberModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        slug={slug}
        onOpenExisting={openCardFor}
      />
      <MemberFilterPanel
        slug={slug}
        open={filterOpen}
        onOpenChange={setFilterOpen}
        filters={filters}
        onChange={setFilters}
      />
      {/* Single shared MemberCard side-panel — rendered at this Members
          level (not inside MemberList) so non-list callers (the
          AddMemberModal dedup affordance, future surfaces) can drive it
          via openCardFor. Stays mounted across row switches; setting
          cardMemberID = null unmounts after the close animation. */}
      <MemberCard
        slug={slug}
        memberID={cardMemberID}
        open={cardMemberID !== null}
        onOpenChange={(open) => {
          if (!open) closeCard();
        }}
        onOpenMember={openCardFor}
      />
    </div>
  );
}

// Fixed-width leading and trailing columns. CHECKBOX_COL hosts the row's
// selection checkbox, CHEVRON_COL the expand button (only painted when the
// row has guardians), ACTIONS_COL the sticky-right 3-dot menu. Always
// reserved on every row so columns line up cleanly across rows.
const CHECKBOX_COL = '2.5rem';
const AVATAR_COL = '2.5rem';
const CHEVRON_COL = '2rem';
const ACTIONS_COL = '4rem';

// Build the grid template: checkbox, avatar bubble, chevron, one
// minmax(160px, 1fr) per data column (so columns don't squeeze too
// narrow on small viewports — instead the table triggers horizontal
// scroll, which is exactly what enables the sticky-right elevation
// effect), and the actions cell at the end.
function gridTemplate(columns: ColumnId[]): string {
  const data = columns.map(() => 'minmax(160px, 1fr)').join(' ');
  return `${CHECKBOX_COL} ${AVATAR_COL} ${CHEVRON_COL} ${data} ${ACTIONS_COL}`;
}

// fetchAllMatchingIds paginates through every member id matching the current
// search filter. Used by the Gmail-style "Select all M matching" link when
// the user wants to bulk-act on more than the current page.
//
// Pages at the server's hard maxLimit (200) so we minimise round-trips. The
// 5000 hard cap matches the comment on the caller — it's a safety valve, not
// an expected hit; large Swedish clubs top out around 1500 active members.
const PAGE_SIZE = 200;
const SELECT_ALL_HARD_CAP = 5000;
async function fetchAllMatchingIds(
  token: string | null,
  slug: string,
  search: string,
): Promise<string[]> {
  const ids: string[] = [];
  let offset = 0;
  while (ids.length < SELECT_ALL_HARD_CAP) {
    const remaining = SELECT_ALL_HARD_CAP - ids.length;
    const limit = Math.min(PAGE_SIZE, remaining);
    const res = await listMembers(token, slug, { search, limit, offset });
    ids.push(...res.members.map((m) => m.id));
    if (res.members.length < limit) break;
    offset += limit;
  }
  return ids;
}

function MemberList({
  members,
  total,
  search,
  columns,
  slug,
  sortState,
  selected,
  selectedGuardians,
  selectedAllMatching,
  setSelected,
  setSelectedGuardians,
  setSelectedAllMatching,
  onClearSelection,
  openCardFor,
}: {
  members: Member[];
  total: number;
  search: string;
  columns: ColumnId[];
  slug: string;
  sortState: TableSortState<ColumnId>;
  // Selection state owned by the Members parent so the header Actions
  // button can also access it.
  selected: Set<string>;
  selectedGuardians: Set<string>;
  selectedAllMatching: boolean;
  setSelected: Dispatch<SetStateAction<Set<string>>>;
  setSelectedGuardians: Dispatch<SetStateAction<Set<string>>>;
  setSelectedAllMatching: Dispatch<SetStateAction<boolean>>;
  onClearSelection: () => void;
  // MemberCard side-panel opener owned by the Members parent. Row clicks
  // route through this so the AddMemberModal dedup affordance and any
  // future page-level surfaces share one opener.
  openCardFor: (memberID: string) => void;
}) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const template = gridTemplate(columns);

  // Track which rows are expanded to show their guardians. Single-list state
  // (rather than per-row useState) so collapsing all is one assignment, and
  // expanded state survives row-row hover.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Auto-expand rows the backend flagged as matched_via_guardian — they
  // landed in the result purely because a guardian's name/email hit the
  // search, so the matching guardian would otherwise be hidden behind the
  // chevron. Union (not assign) so the user's own toggles in this session
  // aren't blown away when results refetch.
  useEffect(() => {
    const toExpand = members
      .filter((m) => m.matched_via_guardian && m.has_guardians)
      .map((m) => m.id);
    if (toExpand.length === 0) return;
    setExpanded((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of toExpand) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [members]);

  // MemberCard side-panel opener arrives via the openCardFor prop; the
  // owning state lives on the Members parent so the AddMemberModal dedup
  // affordance and any future page-level surfaces can drive the same
  // panel.

  // Guardian selection state. Tracked separately from `selected` (which
  // holds member-row IDs) so the bulk-actions bar can show the breakdown
  // ("3 medlemmar, 2 målsmän") and so a future "select all guardians"
  // shortcut can target just this set. The IDs are still member.id values
  // (a guardian is a members.member row), but they enter via guardian-row
  // checkboxes inside an expanded parent.

  function toggleGuardianSelected(guardianMemberID: string, checked: boolean) {
    setSelectedGuardians((prev) => {
      const next = new Set(prev);
      if (checked) next.add(guardianMemberID);
      else next.delete(guardianMemberID);
      return next;
    });
    if (selectedAllMatching) setSelectedAllMatching(false);
  }

  const pageIDs = members.map((m) => m.id);
  const selectedOnPage = pageIDs.filter((id) => selected.has(id));
  const allOnPageSelected = pageIDs.length > 0 && selectedOnPage.length === pageIDs.length;
  const someOnPageSelected = selectedOnPage.length > 0 && !allOnPageSelected;

  function toggleRowSelected(id: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
    if (selectedAllMatching) setSelectedAllMatching(false);
  }
  function clearSelection() {
    onClearSelection();
  }

  // Header dropdown actions: ALWAYS select-all-matching (across all
  // pages) for the active search filter. The previous on-page-only mode
  // was confusing — selecting "all guardians" then realising it only
  // covered guardians visible on this page is a footgun. Now the dropdown
  // is the canonical "select all matching" surface, and the redundant
  // "Markera alla X matchande" link in the bulk bar is gone.
  const selectAllMembersMatchingMutation = useMutation({
    mutationFn: () => fetchAllMatchingIds(token, slug, search),
    onSuccess: (ids) => {
      setSelected(new Set(ids));
      setSelectedAllMatching(true);
    },
  });
  const selectOnlyGuardiansMutation = useMutation({
    mutationFn: () => listGuardianIDsForFilter(token, slug, search),
    onSuccess: (ids) => {
      setSelected(new Set());
      setSelectedGuardians(new Set(ids));
      setSelectedAllMatching(true);
    },
  });
  const selectMembersAndGuardiansMutation = useMutation({
    mutationFn: async () => {
      // Two parallel fetches so the UI doesn't sit on a serial wait.
      const [memberIDs, guardianIDs] = await Promise.all([
        fetchAllMatchingIds(token, slug, search),
        listGuardianIDsForFilter(token, slug, search),
      ]);
      return { memberIDs, guardianIDs };
    },
    onSuccess: ({ memberIDs, guardianIDs }) => {
      setSelected(new Set(memberIDs));
      setSelectedGuardians(new Set(guardianIDs));
      setSelectedAllMatching(true);
    },
  });

  // True when ANY of the three header-dropdown mutations is in flight.
  // Used to disable the menu items + show the "Loading…" copy so a
  // double-click doesn't fire two select-all-matching calls in parallel.
  const selectingAllMatching =
    selectAllMembersMatchingMutation.isPending ||
    selectOnlyGuardiansMutation.isPending ||
    selectMembersAndGuardiansMutation.isPending;

  // The four select-all-matching menu items, shared verbatim between the
  // desktop header checkbox dropdown and the mobile control bar so the two
  // surfaces never drift. Both wrap it in their own <DropdownMenuContent>.
  const selectionMenuItems = (
    <>
      <DropdownMenuItem
        disabled={selectingAllMatching || total === 0}
        onSelect={() => selectAllMembersMatchingMutation.mutate()}
      >
        {selectingAllMatching
          ? t('members.row.loadingGuardians')
          : t('members.row.selectAllMembers')}
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={selectingAllMatching || total === 0}
        onSelect={() => selectMembersAndGuardiansMutation.mutate()}
      >
        {selectingAllMatching
          ? t('members.row.loadingGuardians')
          : t('members.row.selectMembersPlusGuardians')}
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={selectingAllMatching || total === 0}
        onSelect={() => selectOnlyGuardiansMutation.mutate()}
      >
        {selectingAllMatching
          ? t('members.row.loadingGuardians')
          : t('members.row.selectOnlyGuardians')}
      </DropdownMenuItem>
      {(selected.size > 0 || selectedGuardians.size > 0) && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => clearSelection()}>
            {t('members.bulk.clear')}
          </DropdownMenuItem>
        </>
      )}
    </>
  );

  // Horizontal-scroll container: when its content overflows AND the user
  // hasn't scrolled to the right edge, the actions column renders an
  // elevation shadow. When the table fits or we're at the right edge, the
  // shadow drops and the actions column reads as a normal trailing cell.
  const scrollRef = useRef<HTMLDivElement>(null);
  const elevated = useHorizontalScrollEdge(scrollRef);

  // Single-row delete via the CommandIntent flow: create a pending intent
  // with one member.soft_delete operation, approve it. Lands in the
  // activity log and is auditable. (M4a SoftDeleteMember returns no
  // inverse, so the intent isn't activity-log-revertable; recovery is via
  // 30-day soft-delete retention.)
  const deleteMutation = useMutation({
    mutationFn: async (member: Member) => {
      const intent = await createCommand(token, slug, {
        user_input: `Delete ${member.first_name} ${member.last_name}`,
        plan_summary: `Delete member ${member.first_name} ${member.last_name}`,
        operations: [
          {
            type: 'member.soft_delete',
            args: { member_id: member.id },
          },
        ],
      });
      await approveCommand(token, slug, intent.id);
    },
    onSuccess: (_data, member) => {
      // The deleted row may also be in the selection set; drop it so the
      // bulk bar's count stays honest.
      setSelected((prev) => {
        if (!prev.has(member.id)) return prev;
        const next = new Set(prev);
        next.delete(member.id);
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
      void queryClient.invalidateQueries({ queryKey: ['commands', slug] });
    },
  });

  // Bulk delete: a single CommandIntent containing N member.soft_delete
  // operations. Lands in the activity log as one entry ("Delete 5 members")
  // rather than N separate ones, which keeps the log readable.
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const intent = await createCommand(token, slug, {
        user_input: `Delete ${ids.length} members`,
        plan_summary: `Delete ${ids.length} members`,
        operations: ids.map((id) => ({
          type: 'member.soft_delete',
          args: { member_id: id },
        })),
      });
      await approveCommand(token, slug, intent.id);
    },
    onSuccess: () => {
      clearSelection();
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
      void queryClient.invalidateQueries({ queryKey: ['commands', slug] });
    },
  });

  // Combined selection size: member rows + guardian rows. The bulk bar's
  // headline shows the breakdown ("3 medlemmar, 2 målsmän"); the actions
  // operate on the union since both kinds are members.member rows under
  // the hood.
  const combinedSelectedSize = selected.size + selectedGuardians.size;

  // Excel export: paginated members fetch + per-parent guardian lookups,
  // assembled so each selected member is followed by its selected
  // guardians in the sheet. includeGuardians=true is required because
  // pure guardians (people who exist only as guardian of someone else)
  // are filtered out of the default members list — without the flag,
  // ticked guardians silently dropped out of the export.
  const [exportPending, setExportPending] = useState(false);

  // Quick-email dialog state. The recipient set is the union of selected
  // members + selected guardians (both are members.member rows with an email
  // column), snapshotted live from the current selection.
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const emailRecipientIds = useMemo(
    () => [...new Set([...selected, ...selectedGuardians])],
    [selected, selectedGuardians],
  );

  async function handleExportExcel() {
    if (!token || !slug || exportPending) return;
    const memberIds = new Set(selected);
    const guardianIds = new Set(selectedGuardians);
    if (memberIds.size + guardianIds.size === 0) return;
    setExportPending(true);
    try {
      const allSelectedIds = new Set([...memberIds, ...guardianIds]);
      const memberById = new Map<string, Member>();
      let fetchOffset = 0;
      while (true) {
        const res = await listMembers(token, slug, {
          limit: 200,
          offset: fetchOffset,
          includeGuardians: true,
        });
        for (const m of res.members) {
          if (allSelectedIds.has(m.id)) memberById.set(m.id, m);
        }
        if (res.members.length < 200) break;
        fetchOffset += 200;
      }

      // For each selected parent member, fetch its guardian links so we
      // can interleave its selected guardians right beneath it in the
      // sheet. Fired in parallel to keep wall time down.
      const guardianLinks = await Promise.all(
        [...memberIds].map(async (id) => {
          const m = memberById.get(id);
          if (!m?.has_guardians) return { parentID: id, links: [] as Guardian[] };
          const res = await listGuardians(token, slug, id);
          return { parentID: id, links: res.guardians };
        }),
      );

      type ExportRow = {
        kind: 'member' | 'guardian';
        member: Member;
        linkedTo: string;
        relationship: GuardianRelationship;
      };
      const exportRows: ExportRow[] = [];
      const consumedGuardians = new Set<string>();

      for (const { parentID, links } of guardianLinks) {
        const parent = memberById.get(parentID);
        if (!parent) continue;
        exportRows.push({ kind: 'member', member: parent, linkedTo: '', relationship: '' });
        const parentName = `${parent.first_name} ${parent.last_name}`.trim();
        for (const g of links) {
          if (!guardianIds.has(g.member_id) || consumedGuardians.has(g.member_id)) continue;
          const guardianMember = memberById.get(g.member_id);
          if (!guardianMember) continue;
          exportRows.push({
            kind: 'guardian',
            member: guardianMember,
            linkedTo: parentName,
            relationship: g.relationship,
          });
          consumedGuardians.add(g.member_id);
        }
      }

      // Orphan guardians — selected but their parent wasn't. Look up
      // dependents (best-effort) so the "Linked to" column still shows
      // whom they're guardian for.
      const orphanIDs = [...guardianIds].filter((id) => !consumedGuardians.has(id));
      const orphanResolutions = await Promise.all(
        orphanIDs.map(async (id) => {
          try {
            const res = await listDependents(token, slug, id);
            const dep = res.dependents[0];
            return {
              id,
              linkedTo: dep ? `${dep.first_name} ${dep.last_name}`.trim() : '',
              relationship: (dep?.relationship ?? '') as GuardianRelationship,
            };
          } catch {
            return { id, linkedTo: '', relationship: '' as GuardianRelationship };
          }
        }),
      );
      for (const { id, linkedTo, relationship } of orphanResolutions) {
        const m = memberById.get(id);
        if (!m) continue;
        exportRows.push({ kind: 'guardian', member: m, linkedTo, relationship });
      }

      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(t('members.actionsDropdown.sheetName'));

      ws.addRow([
        t('members.actionsDropdown.columnType'),
        t('members.actionsDropdown.columnLinkedTo'),
        t('members.actionsDropdown.columnRelationship'),
        t('members.column.firstName'),
        t('members.column.lastName'),
        t('members.column.email'),
        t('members.column.phone'),
        t('members.column.personalId'),
        t('members.column.streetAddress'),
        t('members.column.postalCode'),
        t('members.column.city'),
        t('members.actionsDropdown.columnMemberSince'),
      ]);

      for (const r of exportRows) {
        const typeLabel =
          r.kind === 'member'
            ? t('members.actionsDropdown.typeMember')
            : t('members.actionsDropdown.typeGuardian');
        const relationshipLabel =
          r.kind === 'guardian'
            ? t(
                r.relationship
                  ? `members.guardians.relationship.${r.relationship}`
                  : 'members.guardians.relationship.unknown',
              )
            : '';
        ws.addRow([
          typeLabel,
          r.linkedTo,
          relationshipLabel,
          r.member.first_name,
          r.member.last_name,
          r.member.email,
          r.member.phone,
          r.member.personal_id ?? '',
          r.member.street_address,
          r.member.postal_code,
          r.member.city,
          r.member.created_at ? new Date(r.member.created_at) : '',
        ]);
      }

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `members-${slug}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportPending(false);
    }
  }

  // Bulk-action menu items. New bulk operations (add-to-team, change-role,
  // ...) get added here as their backend Operations land. Order matters —
  // destructive actions sit at the bottom of the dropdown and are visually
  // separated by a divider.
  const bulkActions: BulkAction[] = [
    {
      id: 'export',
      label: t('members.actionsDropdown.exportExcel'),
      icon: FileXls,
      disabled: exportPending,
      onSelect: () => { void handleExportExcel(); },
    },
    {
      id: 'email',
      label: t('members.email.action'),
      icon: Envelope,
      onSelect: () => { setEmailDialogOpen(true); },
    },
    {
      id: 'delete',
      label: t('members.bulk.delete'),
      icon: Trash,
      destructive: true,
      disabled: bulkDeleteMutation.isPending,
      onSelect: async () => {
        const effectiveCount = selectedAllMatching ? total : combinedSelectedSize;
        const ok = await confirm({
          title: t('members.bulk.deleteTitle', { count: effectiveCount }),
          body: t('members.bulk.deleteConfirm', { count: effectiveCount }),
          confirmLabel: t('members.bulk.deleteCta'),
          tone: 'destructive',
        });
        if (ok) {
          // Union of both selection sets — guardians are members.member
          // rows so the same member.soft_delete operation works on either.
          const ids = new Set([...selected, ...selectedGuardians]);
          bulkDeleteMutation.mutate([...ids]);
        }
      },
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {combinedSelectedSize > 0 && (
        <BulkActionsBar
          count={combinedSelectedSize}
          memberCount={selected.size}
          guardianCount={selectedGuardians.size}
          allMatching={selectedAllMatching}
          onClear={clearSelection}
          actions={bulkActions}
        />
      )}

      <QuickEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        slug={slug}
        token={token}
        memberIds={emailRecipientIds}
        recipientCount={combinedSelectedSize}
        onSent={clearSelection}
      />

      <div
        className="rounded-2xl border border-border bg-background shadow-sm"
        // clip-path clips to the same rounded shape as rounded-2xl
        // (var(--radius-2xl) = 1.5rem), hiding any scrollbar arrow or
        // sub-pixel artifact that paints just past the rounded corners.
        // Unlike overflow:hidden, clip-path doesn't change the
        // sticky-positioning containing block, so the sticky-right
        // actions column keeps working.
        style={{ clipPath: 'inset(0 round var(--radius-2xl))' }}
      >
        {/* Mobile control bar — sm:hidden. On mobile the header row below is
            hidden (rows collapse to name/email cards), which also removes the
            two controls that live in that header: the select-all-matching
            dropdown and the per-column sort. This bar restores both for
            phones without forcing the full table to scroll horizontally. */}
        <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2 sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex min-h-[44px] items-center gap-2 rounded-pill px-2 text-sm font-medium text-foreground transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span
                  role="checkbox"
                  aria-checked={
                    allOnPageSelected
                      ? 'true'
                      : someOnPageSelected
                        ? 'mixed'
                        : 'false'
                  }
                  aria-label={t('members.row.selectAll')}
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded border border-border transition-colors',
                    allOnPageSelected || someOnPageSelected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'bg-background',
                  )}
                >
                  {allOnPageSelected ? (
                    <Check className="h-3.5 w-3.5" weight="bold" aria-hidden />
                  ) : someOnPageSelected ? (
                    <Minus className="h-3.5 w-3.5" weight="bold" aria-hidden />
                  ) : null}
                </span>
                <span>{t('members.mobileBar.select')}</span>
                <CaretDown className="h-3.5 w-3.5 opacity-60" aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[240px]">
              {selectionMenuItems}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('members.mobileBar.sort')}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-pill px-3 text-sm font-medium text-foreground transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ArrowsDownUp className="h-4 w-4 opacity-70" aria-hidden />
                <span className="truncate">
                  {sortState.sort
                    ? t(COLUMN_LABEL_KEY[sortState.sort])
                    : t('members.mobileBar.sort')}
                </span>
                {sortState.sort &&
                  (sortState.dir === 'asc' ? (
                    <CaretUp className="h-3.5 w-3.5 shrink-0" weight="bold" aria-hidden />
                  ) : (
                    <CaretDown className="h-3.5 w-3.5 shrink-0" weight="bold" aria-hidden />
                  ))}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[220px]">
              {columns.map((id) => {
                const active = sortState.sort === id;
                return (
                  <DropdownMenuItem
                    key={id}
                    onSelect={() => sortState.toggle(id)}
                    className={cn(active && 'text-foreground')}
                  >
                    <span className="flex-1 truncate">
                      {t(COLUMN_LABEL_KEY[id])}
                    </span>
                    {active &&
                      (sortState.dir === 'asc' ? (
                        <CaretUp className="h-3.5 w-3.5 shrink-0" weight="bold" aria-hidden />
                      ) : (
                        <CaretDown
                          className="h-3.5 w-3.5 shrink-0"
                          weight="bold"
                          aria-hidden
                        />
                      ))}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div ref={scrollRef} className="overflow-x-auto">
          <div className="min-w-fit divide-y divide-border">
            {/* Header row — sm+ only; mobile rows stack name/email instead.
                NO horizontal padding on the outer grid: each cell owns its
                own padding (matching the data rows below) so the checkbox
                column lines up flush at the same x-position in both. */}
            <div
              className="hidden bg-muted/40 py-3 sm:grid"
              style={{ gridTemplateColumns: template }}
              role="row"
            >
              {/* Header "checkbox" is actually a button styled as a
                  checkbox: clicking it opens a dropdown of selection
                  variants (members only, members + loaded guardians,
                  guardians only, clear). The visual state still mirrors
                  the page-selection so the column header reads at a
                  glance as checked / indeterminate / unchecked.
                  role="checkbox" + aria-checked keeps assistive tech
                  treating it as a checkbox even though the underlying
                  element is a button. */}
              <div className="flex items-center justify-center">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={
                        allOnPageSelected
                          ? 'true'
                          : someOnPageSelected
                            ? 'mixed'
                            : 'false'
                      }
                      aria-label={t('members.row.selectAll')}
                      className={cn(
                        'flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded border border-border transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        (allOnPageSelected || someOnPageSelected)
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'bg-background hover:bg-muted',
                      )}
                    >
                      {allOnPageSelected ? (
                        <Check className="h-3 w-3" weight="bold" aria-hidden />
                      ) : someOnPageSelected ? (
                        <Minus className="h-3 w-3" weight="bold" aria-hidden />
                      ) : null}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[240px]">
                    {selectionMenuItems}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {/* Empty placeholders so the grid keeps its AVATAR_COL +
                  CHEVRON_COL slots and data-row avatars + chevrons line
                  up with their columns. */}
              <div aria-hidden="true" />
              <div aria-hidden="true" />
              {columns.map((id) => (
                <span key={id} className="px-2" role="columnheader">
                  <SortableHeaderButton
                    column={id}
                    label={t(COLUMN_LABEL_KEY[id])}
                    state={sortState}
                    className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
                  />
                </span>
              ))}
              <StickyEndCell
                elevated={false}
                bgClass="bg-muted/40"
                className="justify-end"
              >
                <span className="sr-only">{t('rowActions.columnHeader')}</span>
              </StickyEndCell>
            </div>

            {members.map((m) => (
              <MemberRow
                key={m.id}
                slug={slug}
                member={m}
                columns={columns}
                template={template}
                expanded={expanded.has(m.id)}
                onToggle={() => toggleExpanded(m.id)}
                selected={selected.has(m.id)}
                onSelectChange={(checked) => toggleRowSelected(m.id, checked)}
                elevated={elevated}
                onOpenCard={openCardFor}
                onDelete={() => deleteMutation.mutate(m)}
                deleting={deleteMutation.isPending && deleteMutation.variables?.id === m.id}
                onDeleteGuardian={(g) =>
                  // A guardian IS a member.member row, so reuse the same
                  // member.soft_delete CommandIntent. Synthesize a Member-
                  // shaped object from the Guardian fields the mutation
                  // reads (id + names for the activity-feed summary).
                  deleteMutation.mutate({
                    id: g.member_id,
                    first_name: g.first_name,
                    last_name: g.last_name,
                    email: g.email,
                    phone: g.phone,
                    city: g.city,
                    street_address: g.street_address,
                    postal_code: g.postal_code,
                    personal_id: g.personal_id ?? '',
                    personal_id_status: g.personal_id_status,
                    has_guardians: false,
                    has_photo: false,
                    has_contact_issue: false,
                    has_push: false,
                    created_at: '',
                  })
                }
                deletingGuardianID={
                  deleteMutation.isPending ? (deleteMutation.variables?.id ?? null) : null
                }
                selectedGuardians={selectedGuardians}
                onSelectGuardian={toggleGuardianSelected}
              />
            ))}
          </div>
        </div>
      </div>

      {/* MemberCard side-panel is rendered at the Members parent level
          (see top of file) so the AddMemberModal dedup affordance and any
          future page-level surfaces can drive it via the openCardFor prop
          threaded through here. */}
    </div>
  );
}

// BulkAction is one entry in the BulkActionsBar dropdown. Same shape as
// RowAction so future code can share helpers if it ever makes sense.
interface BulkAction {
  id: string;
  label: string;
  icon?: Icon;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

function BulkActionsBar({
  count,
  memberCount,
  guardianCount,
  allMatching,
  onClear,
  actions,
}: {
  // Combined selection count (members + guardians). Drives the "X
  // selected" headline.
  count: number;
  // Per-kind counts so the bar can render the breakdown
  // ("3 medlemmar, 2 målsmän markerade") when both kinds have rows
  // selected.
  memberCount: number;
  guardianCount: number;
  // True when the user picked "all matching" via the header dropdown.
  // Drives the alternate copy ("Alla N matchande medlemmar markerade").
  allMatching: boolean;
  onClear: () => void;
  // Action items shown in the dropdown. Destructive items get separated
  // and styled with the danger treatment, same as RowActions.
  actions: BulkAction[];
}) {
  const { t } = useTranslation('common');
  const safe = actions.filter((a) => !a.destructive);
  const danger = actions.filter((a) => a.destructive);

  // Pick the headline copy based on what's selected. In allMatching mode
  // we keep the existing single-count headline. Otherwise, when both
  // kinds are present, we surface the breakdown so the user can confirm
  // which subset they're acting on.
  let headline: string;
  if (allMatching) {
    headline = t('members.bulk.selectedAllMatching', { count });
  } else if (memberCount > 0 && guardianCount > 0) {
    headline = t('members.bulk.selectedBreakdown', {
      members: memberCount,
      guardians: guardianCount,
    });
  } else if (guardianCount > 0 && memberCount === 0) {
    headline = t('members.bulk.selectedGuardians', { count: guardianCount });
  } else {
    headline = t('members.bulk.selected', { count });
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border bg-card px-3 py-2 shadow-sm">
      <span className="font-heading text-sm font-semibold text-foreground">
        {headline}
      </span>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClear}>
          <X className="h-4 w-4" />
          {t('members.bulk.clear')}
        </Button>

        {actions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                {t('members.bulk.actions')}
                <CaretDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {safe.map((a) => (
                <DropdownMenuItem
                  key={a.id}
                  disabled={!!a.disabled}
                  onSelect={a.onSelect}
                >
                  {a.icon && <a.icon className="h-4 w-4" />}
                  {a.label}
                </DropdownMenuItem>
              ))}
              {safe.length > 0 && danger.length > 0 && <DropdownMenuSeparator />}
              {danger.map((a) => (
                <DropdownMenuItem
                  key={a.id}
                  disabled={!!a.disabled}
                  onSelect={a.onSelect}
                  className="text-destructive focus:text-destructive"
                >
                  {a.icon && <a.icon className="h-4 w-4" />}
                  {a.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

interface MemberRowProps {
  slug: string;
  member: Member;
  columns: ColumnId[];
  template: string;
  expanded: boolean;
  onToggle: () => void;
  selected: boolean;
  onSelectChange: (checked: boolean) => void;
  elevated: boolean;
  // Open the MemberCard side panel for any member id (the parent row's
  // own member, or any of its guardians — guardians ARE members so the
  // card works identically). Receives the id to open so MemberRow can
  // route both its own button and the nested guardian-row buttons
  // through the same handler.
  onOpenCard: (memberID: string) => void;
  onDelete: () => void;
  deleting: boolean;
  // Guardian delete callback + currently-deleting guardian id. Threaded down
  // to GuardiansSection / GuardianRow so the 3-dot menu on a guardian row
  // can drive the same `member.soft_delete` flow as the parent rows above.
  onDeleteGuardian: (g: Guardian) => void;
  deletingGuardianID: string | null;
  // Guardian-row selection threaded down. Each guardian row renders its
  // own checkbox bound to this set so bulk actions can target guardians
  // independently of (and alongside) member-row selections.
  selectedGuardians: Set<string>;
  onSelectGuardian: (guardianMemberID: string, checked: boolean) => void;
}

// MembershipBadge renders the membership-fee status for the current fiscal
// year: lime "Paid", neutral "Unpaid" (an invoice exists but isn't paid), a
// muted "No invoice", or "—" when the org hasn't configured a membership-fee
// type (membership omitted by the backend).
function MembershipBadge({ membership }: { membership: MemberMembership | undefined }) {
  const { t } = useTranslation('common');
  if (!membership) {
    return <span className="text-muted-foreground">{t('members.membership.notConfigured')}</span>;
  }
  if (membership.paid) {
    return <Badge variant="default">{t('members.membership.paid')}</Badge>;
  }
  if (membership.status) {
    return <Badge variant="outline">{t('members.membership.unpaid')}</Badge>;
  }
  return <span className="text-muted-foreground">{t('members.membership.noInvoice')}</span>;
}

function MemberRow({
  slug,
  member,
  columns,
  template,
  expanded,
  onToggle,
  selected,
  onSelectChange,
  elevated,
  onOpenCard,
  onDelete,
  deleting,
  onDeleteGuardian,
  selectedGuardians,
  onSelectGuardian,
  deletingGuardianID,
}: MemberRowProps) {
  const { t } = useTranslation('common');
  const confirm = useConfirm();
  const value = (id: ColumnId): string => {
    switch (id) {
      case 'first_name':
        return member.first_name;
      case 'last_name':
        return member.last_name;
      case 'personal_id':
        return member.personal_id ?? '';
      case 'email':
        return member.email;
      case 'phone':
        return member.phone;
      case 'street_address':
        return member.street_address;
      case 'postal_code':
        return member.postal_code;
      case 'city':
        return member.city;
      case 'membership':
        // Rendered as a badge in the cell map below; the text path is unused.
        return '';
    }
  };
  // Severity tiers for the warning triangle:
  //   - 'severe' (red): personal_id is missing or fails validation. Material
  //     data-quality issue — registration paperwork blocks until fixed.
  //   - 'warn' (orange): no contact path AND no contactable guardian.
  //     Annoying but not blocking.
  //   - null: complete row, no triangle.
  // Severe wins when both apply — single triangle, the worse-of-the-two.
  const warningSeverity: 'severe' | 'warn' | null =
    member.personal_id_status === 'none' || member.personal_id_status === 'needs_review'
      ? 'severe'
      : member.has_contact_issue
        ? 'warn'
        : null;
  const warningLabel = warningSeverity
    ? t(
        warningSeverity === 'severe'
          ? member.personal_id_status === 'none'
            ? 'members.warnings.personalIdMissing'
            : 'members.warnings.personalIdNeedsReview'
          : 'members.warnings.contactMissing',
      )
    : '';
  const fullName = `${member.first_name} ${member.last_name}`.trim();

  // 3-dot menu surface. "Open profile" is the canonical entry point to
  // the MemberCard side panel; it's also exposed as a hover-revealed
  // fast-action icon below so a single click on the row's right edge
  // opens the card without going through the menu.
  const menuActions: RowAction[] = [
    {
      id: 'open',
      label: t('members.actions.openProfile'),
      icon: User,
      onSelect: () => onOpenCard(member.id),
    },
    {
      id: 'delete',
      label: t('members.actions.delete'),
      icon: Trash,
      destructive: true,
      disabled: deleting,
      onSelect: async () => {
        const ok = await confirm({
          title: t('members.actions.deleteTitle', { name: fullName }),
          body: t('members.actions.deleteConfirm', { name: fullName }),
          confirmLabel: t('members.actions.deleteCta'),
          tone: 'destructive',
        });
        if (ok) onDelete();
      },
    },
  ];

  // Hover-revealed fast actions: just "Open profile" for now. The
  // RowActions primitive renders these as small icon buttons that fade
  // in on row hover at md+; they stay hidden on mobile (no hover).
  const fastActions: RowAction[] = [
    {
      id: 'open',
      label: t('members.actions.openProfile'),
      icon: User,
      onSelect: () => onOpenCard(member.id),
    },
  ];

  // Background classes vary by selected/expanded/hover state. Tracked once
  // here and passed to the sticky-right cell so it paints in lockstep.
  // The row uses translucent tints (cheap; layered over the table's white
  // backdrop). The sticky cell MUST be fully opaque — the data column
  // scrolls underneath it, and a translucent sticky cell shows the data
  // text bleeding through. So the sticky variants pick the equivalent
  // opaque values from the design tokens.
  const rowBg = selected
    ? 'bg-accent-soft hover:bg-accent-soft'
    : expanded
      ? 'bg-muted/30 hover:bg-muted/40'
      : 'hover:bg-muted/40';
  const stickyBg = selected
    ? 'bg-[oklch(0.985_0.025_110)] group-hover:bg-[oklch(0.985_0.025_110)]'
    : expanded
      ? 'bg-muted group-hover:bg-muted'
      : 'bg-background group-hover:bg-muted';

  return (
    <div className="divide-y divide-border">
      <div
        className={cn('group grid items-stretch transition-colors', rowBg)}
        style={{ gridTemplateColumns: template }}
        role="row"
      >
        {/* Selection checkbox column. */}
        <div className="flex items-center justify-center">
          <Checkbox
            checked={selected}
            onChange={(e) => onSelectChange(e.currentTarget.checked)}
            aria-label={t('members.row.select', { name: fullName })}
          />
        </div>

        {/* Avatar bubble — photo when set, initials otherwise. */}
        <div className="flex items-center justify-center">
          <MemberAvatar
            slug={slug}
            memberID={member.id}
            firstName={member.first_name}
            lastName={member.last_name}
            hasPhoto={member.has_photo}
          />
        </div>

        {/* Chevron column — only renders the button when there are guardians. */}
        <div className="flex items-center justify-center">
          {member.has_guardians && (
            <button
              type="button"
              onClick={onToggle}
              aria-label={
                expanded
                  ? t('members.guardians.collapse')
                  : t('members.guardians.expand')
              }
              aria-expanded={expanded}
              className="flex h-8 w-8 items-center justify-center rounded-pill text-foreground/70 transition-colors hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CaretRight
                className={cn(
                  'h-4 w-4 transition-transform duration-150',
                  expanded && 'rotate-90',
                )}
              />
            </button>
          )}
        </div>

        {/* Mobile: two-line stack rendered into the FIRST data column,
            spanning the rest. Hidden at sm+. */}
        <div
          className="flex min-w-0 flex-col gap-0.5 px-2 py-3 text-left sm:hidden"
          style={{ gridColumn: `4 / -2` }}
        >
          <span className="flex items-center gap-1.5 font-heading text-sm font-semibold text-foreground">
            <span>{fullName}</span>
            {member.has_push && <PushIndicator label={t('members.push.indicator')} />}
            {member.has_guardians && (
              <GuardianIndicator label={t('members.guardians.indicator')} />
            )}
            {warningSeverity && (
              <MemberWarningBadge severity={warningSeverity} label={warningLabel} />
            )}
          </span>
          {member.email && (
            <span className="truncate text-xs text-muted-foreground">{member.email}</span>
          )}
        </div>

        {/* Desktop: one cell per visible column, sm+ only. */}
        {columns.map((id) => (
          <span
            key={id}
            className={cn(
              'hidden items-center gap-1.5 truncate px-2 py-3 text-sm sm:flex',
              (id === 'first_name' || id === 'last_name') &&
                'font-heading font-semibold text-foreground',
              id !== 'first_name' && id !== 'last_name' && 'text-foreground/80',
              id === 'personal_id' && 'tabular-nums',
            )}
            role="cell"
          >
            {id === 'membership' ? (
              <MembershipBadge membership={member.membership} />
            ) : (
              <span className="truncate">{value(id) || ''}</span>
            )}
            {id === 'last_name' && member.has_push && (
              <PushIndicator label={t('members.push.indicator')} />
            )}
            {id === 'last_name' && member.has_guardians && (
              <GuardianIndicator label={t('members.guardians.indicator')} />
            )}
            {id === 'last_name' && warningSeverity && (
              <MemberWarningBadge severity={warningSeverity} label={warningLabel} />
            )}
          </span>
        ))}

        <StickyEndCell elevated={elevated} bgClass={stickyBg}>
          <RowActions fastActions={fastActions} menuActions={menuActions} />
        </StickyEndCell>
      </div>

      {/* Inline guardians panel — sibling rows below the data row when
          expanded. Each guardian uses the SAME grid template as the parent
          so columns line up; the panel reads like nested rows of the same
          table rather than a side-car detail card. */}
      {expanded && member.has_guardians && (
        <GuardiansSection
          memberID={member.id}
          columns={columns}
          template={template}
          elevated={elevated}
          onDeleteGuardian={onDeleteGuardian}
          deletingGuardianID={deletingGuardianID}
          selectedGuardians={selectedGuardians}
          onSelectGuardian={onSelectGuardian}
          onOpenCard={onOpenCard}
        />
      )}
    </div>
  );
}

function GuardianIndicator({ label }: { label: string }) {
  return (
    <span title={label} className="shrink-0 text-foreground/60" aria-label={label}>
      <UsersThree size={14} weight="duotone" />
    </span>
  );
}

// PushIndicator marks members whose linked login has a live device token —
// i.e. they have the app installed and push notifications reach them. Sits
// next to the name, same treatment as GuardianIndicator.
function PushIndicator({ label }: { label: string }) {
  return (
    <span title={label} className="shrink-0 text-foreground/60" aria-label={label}>
      <DeviceMobile size={14} weight="duotone" />
    </span>
  );
}

// MemberWarningBadge renders a single triangle whose color encodes severity.
// 'severe' (destructive) flags missing or invalid personal_id; 'warn' (amber)
// flags missing contact info with no contactable guardian. The row picks the
// worse-of-the-two so each member shows at most one triangle.
function MemberWarningBadge({
  severity,
  label,
}: {
  severity: 'severe' | 'warn';
  label: string;
}) {
  return (
    <span title={label} className="shrink-0">
      <Warning
        size={14}
        weight="fill"
        aria-label={label}
        className={severity === 'severe' ? 'text-destructive' : 'text-amber-500'}
      />
    </span>
  );
}

// GuardiansSection lazily fetches the member_guardian rows for a single
// member when its accordion item expands. Radix unmounts AccordionContent on
// collapse, so the query naturally activates only on open. "Add guardian"
// remains disabled — that interactive flow is the next slice. Importer
// already populates this view from the Förälder→subject lookback.
// GuardiansSection renders guardian rows as siblings below the parent member
// row, using the SAME grid template so columns align. Adopting the table's
// own grid means the data reads as nested rows of the same table — same
// columns, same widths — rather than as a side-car detail card.
function GuardiansSection({
  memberID,
  columns,
  template,
  elevated,
  onDeleteGuardian,
  deletingGuardianID,
  selectedGuardians,
  onSelectGuardian,
  onOpenCard,
}: {
  memberID: string;
  columns: ColumnId[];
  template: string;
  // Forwarded from MemberList's useHorizontalScrollEdge so the
  // sticky-right cell on guardian rows can render the elevation shadow
  // in lockstep with the parent member rows above.
  elevated: boolean;
  onDeleteGuardian: (g: Guardian) => void;
  deletingGuardianID: string | null;
  // Open the MemberCard side panel for any member id. Threaded through
  // from MemberList → MemberRow → here so guardian rows can surface the
  // same "Open profile" action as their parent rows.
  onOpenCard: (memberID: string) => void;
  selectedGuardians: Set<string>;
  onSelectGuardian: (guardianMemberID: string, checked: boolean) => void;
}) {
  const { t } = useTranslation('common');
  const { slug = '' } = useParams();
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const confirm = useConfirm();

  const { data, isLoading, error } = useQuery({
    queryKey: ['guardians', slug, memberID],
    queryFn: () => listGuardians(token, slug, memberID),
    enabled: !!slug && !!memberID,
  });

  const guardians = data?.guardians ?? [];

  // No eyebrow header — the angled-arrow chevron in each guardian row's
  // chevron slot already conveys "these are nested under the parent row",
  // and dropping the label tightens the visual hierarchy so the panel
  // reads as part of the same table rather than a separate sub-section.
  return (
    <div className="border-t border-border/60">
      {isLoading && (
        <p className="px-4 py-2 text-sm text-muted-foreground">
          {t('members.guardians.loading')}
        </p>
      )}

      {error && (
        <p role="alert" className="px-4 py-2 text-sm text-destructive">
          {t('members.guardians.error')}
        </p>
      )}

      {!isLoading && !error && guardians.length === 0 && (
        <p className="px-4 py-2 text-sm text-muted-foreground">
          {t('members.guardians.empty.title')}
        </p>
      )}

      {!isLoading && !error && guardians.length > 0 && (
        <div className="divide-y divide-border/60">
          {guardians.map((g) => {
            const fullName = `${g.first_name} ${g.last_name}`.trim();
            return (
              <GuardianRow
                key={g.link_id}
                slug={slug}
                guardian={g}
                columns={columns}
                template={template}
                elevated={elevated}
                onDelete={async () => {
                  // Same destructive-confirm pattern as MemberRow's delete.
                  // Body copy mentions the 30-day recovery window so the user
                  // knows it's reversible from the activity log.
                  const ok = await confirm({
                    title: t('members.actions.deleteTitle', { name: fullName }),
                    body: t('members.actions.deleteConfirm', { name: fullName }),
                    confirmLabel: t('members.actions.deleteCta'),
                    tone: 'destructive',
                  });
                  if (ok) onDeleteGuardian(g);
                }}
                deleting={deletingGuardianID === g.member_id}
                selected={selectedGuardians.has(g.member_id)}
                onSelectChange={(checked) => onSelectGuardian(g.member_id, checked)}
                onOpenCard={() => onOpenCard(g.member_id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// GuardianRow mirrors MemberRow's grid template — same column widths, same
// data dispatch, same warning-triangle rules (red tier only; the orange
// contact tier already shows on the parent member row when relevant). The
// chevron slot carries the angled-arrow nesting indicator; the relation
// badge (icon-only bubble with tooltip) sits immediately after it as the
// leading element of the first data cell. The 3-dot action menu reuses the
// same `member.soft_delete` flow as the parent rows above.
function GuardianRow({
  slug,
  guardian,
  columns,
  template,
  elevated,
  onDelete,
  deleting,
  selected,
  onSelectChange,
  onOpenCard,
}: {
  slug: string;
  guardian: Guardian;
  columns: ColumnId[];
  template: string;
  elevated: boolean;
  onDelete: () => void;
  deleting: boolean;
  selected: boolean;
  onSelectChange: (checked: boolean) => void;
  onOpenCard: () => void;
}) {
  const { t } = useTranslation('common');

  const value = (id: ColumnId): string => {
    switch (id) {
      case 'first_name':
        return guardian.first_name;
      case 'last_name':
        return guardian.last_name;
      case 'personal_id':
        return guardian.personal_id ?? '';
      case 'email':
        return guardian.email;
      case 'phone':
        return guardian.phone;
      case 'street_address':
        return guardian.street_address;
      case 'postal_code':
        return guardian.postal_code;
      case 'city':
        return guardian.city;
      case 'membership':
        // Guardians don't carry a membership-fee status in this list view.
        return '';
    }
  };

  const warningSeverity: 'severe' | null =
    guardian.personal_id_status === 'none' || guardian.personal_id_status === 'needs_review'
      ? 'severe'
      : null;
  const warningLabel = warningSeverity
    ? t(
        guardian.personal_id_status === 'none'
          ? 'members.warnings.personalIdMissing'
          : 'members.warnings.personalIdNeedsReview',
      )
    : '';

  const relationshipLabel = guardian.relationship
    ? t(`members.guardians.relationship.${guardian.relationship}`)
    : t('members.guardians.relationship.unknown');
  const relationshipKnown = !!guardian.relationship;
  const fullName = `${guardian.first_name} ${guardian.last_name}`.trim();

  // Mirror MemberRow's action shape so the 3-dot dropdown + hover icons
  // read identically on guardians and members. Guardians ARE members so
  // "Open profile" jumps to the same MemberCard side panel as their
  // parent row's button.
  const menuActions: RowAction[] = [
    {
      id: 'open',
      label: t('members.actions.openProfile'),
      icon: User,
      onSelect: onOpenCard,
    },
    {
      id: 'delete',
      label: t('members.actions.delete'),
      icon: Trash,
      destructive: true,
      disabled: deleting,
      onSelect: onDelete,
    },
  ];
  const fastActions: RowAction[] = [
    {
      id: 'open',
      label: t('members.actions.openProfile'),
      icon: User,
      onSelect: onOpenCard,
    },
  ];

  // First visible column is always first_name (pinned). If a future
  // refactor makes the first column dynamic, this picks up columns[0].
  const firstColumn: ColumnId = columns[0] ?? 'first_name';

  // Background ramp tracks selected → hover → resting. All values are
  // OPAQUE — the sticky-right actions cell paints over scrolling data
  // beneath it, and any partial-alpha bg there causes data text to
  // bleed through (same fix as MemberRow's stickyBg block).
  const rowBg = selected
    ? 'bg-accent-soft hover:bg-accent-soft'
    : 'hover:bg-muted/30';
  const stickyBg = selected
    ? 'bg-[oklch(0.985_0.025_110)] group-hover:bg-[oklch(0.985_0.025_110)]'
    : 'bg-background group-hover:bg-muted/30';

  return (
    <div
      className={cn('group grid items-stretch transition-colors', rowBg)}
      style={{ gridTemplateColumns: template }}
      role="row"
    >
      {/* Selection checkbox — guardians can be multi-selected just like
          parent member rows. The bulk-actions bar shows the breakdown so
          the user can target members and guardians independently. */}
      <div className="flex items-center justify-center">
        <Checkbox
          checked={selected}
          onChange={(e) => onSelectChange(e.currentTarget.checked)}
          aria-label={t('members.row.select', { name: fullName })}
        />
      </div>

      {/* Avatar bubble — same column as MemberRow so guardians line up. */}
      <div className="flex items-center justify-center">
        <MemberAvatar
          slug={slug}
          memberID={guardian.member_id}
          firstName={guardian.first_name}
          lastName={guardian.last_name}
          hasPhoto={false}
        />
      </div>

      {/* Chevron slot: angled-arrow nesting indicator. */}
      <div className="flex items-center justify-center text-muted-foreground/60">
        <ArrowBendDownRight className="h-3.5 w-3.5" aria-hidden />
      </div>

      {/* Mobile: stacked layout in the first data column, same slot as
          MemberRow so the two render consistently. */}
      <div
        className="flex min-w-0 flex-col gap-0.5 px-2 py-2.5 text-left sm:hidden"
        style={{ gridColumn: `3 / -2` }}
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground/90">
          <RelationshipBadge label={relationshipLabel} known={relationshipKnown} />
          <span>{fullName}</span>
          {warningSeverity && (
            <MemberWarningBadge severity={warningSeverity} label={warningLabel} />
          )}
        </span>
        {(guardian.email || guardian.phone) && (
          <span className="truncate text-xs text-muted-foreground">
            {guardian.email || guardian.phone}
          </span>
        )}
      </div>

      {/* Desktop cells — one per visible column, mirroring MemberRow. The
          relation badge leads the first data cell so it sits "outside the
          angled arrow" rather than inside the last_name column. */}
      {columns.map((id) => (
        <span
          key={id}
          className={cn(
            'hidden items-center gap-1.5 truncate px-2 py-2.5 text-sm sm:flex',
            (id === 'first_name' || id === 'last_name') && 'font-medium text-foreground/90',
            id !== 'first_name' && id !== 'last_name' && 'text-foreground/70',
            id === 'personal_id' && 'tabular-nums',
          )}
          role="cell"
        >
          {id === firstColumn && (
            <RelationshipBadge label={relationshipLabel} known={relationshipKnown} />
          )}
          <span className="truncate">{value(id) || ''}</span>
          {id === 'last_name' && warningSeverity && (
            <MemberWarningBadge severity={warningSeverity} label={warningLabel} />
          )}
        </span>
      ))}

      {/* Sticky-right actions cell. Uses the same StickyEndCell + RowActions
          components as MemberRow so the 3-dot menu reads identically. */}
      <StickyEndCell elevated={elevated} bgClass={stickyBg}>
        <RowActions fastActions={fastActions} menuActions={menuActions} />
      </StickyEndCell>
    </div>
  );
}

// RelationshipBadge: a small circular icon-only bubble that uses a native
// title-attribute tooltip to surface the relationship label on hover. Solid
// background when the relationship is known, dashed/muted when unknown. The
// icon stays the same in both states — the tooltip + contrast carries the
// distinction. Sized to slot inline with the warning triangle next to it.
function RelationshipBadge({ label, known }: { label: string; known: boolean }) {
  return (
    <span
      title={label}
      aria-label={label}
      className={cn(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-pill',
        known
          ? 'bg-muted text-foreground/70'
          : 'border border-dashed border-border text-muted-foreground',
      )}
    >
      <UsersThree size={12} weight="duotone" aria-hidden />
    </span>
  );
}

function MembersEmpty({ onAdd }: { onAdd?: () => void }) {
  const { t } = useTranslation('common');
  return (
    <EmptyState
      icon={<User weight="duotone" />}
      title={t('members.empty.title')}
      body={t('members.empty.body')}
      actions={
        onAdd
          ? [
              {
                label: t('members.addCta'),
                onClick: onAdd,
                icon: <Plus weight="bold" />,
              },
            ]
          : undefined
      }
    />
  );
}
